"use client";

import {
    listAdminUsersApiV1AdminUsersGet,
    removeMemberApiV1AdminMembersDelete,
    moveUserOrganizationApiV1AdminUsersPost,
    createAdminOrganizationApiV1AdminOrganizationsPost,
} from "@/src/api/sdk.gen";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, MoveRight, RefreshCw, Users } from "lucide-react";

type Role = "admin" | "member";

interface OrgMembership {
    orgId: number;
    orgName: string;
    role: Role;
}

interface ClerkUserRow {
    clerkUserId: string;
    email: string | null;
    name: string;
    memberships: OrgMembership[];
}

interface AdminOrg {
    id: number;
    name: string;
}

interface UsersResponse {
    users: ClerkUserRow[];
    organizations: AdminOrg[];
}

interface ToastMessage {
    id: string;
    text: string;
    type: "success" | "error";
}

export function UserOrganizationManager() {
    const [users, setUsers] = useState<ClerkUserRow[]>([]);
    const [organizations, setOrganizations] = useState<AdminOrg[]>([]);
    const [selectedOrgFilter, setSelectedOrgFilter] = useState<string>("all");
    const [targetOrgByUser, setTargetOrgByUser] = useState<Record<string, string>>({});
    const [sourceOrgByUser, setSourceOrgByUser] = useState<Record<string, string>>({});
    const [roleByUser, setRoleByUser] = useState<Record<string, Role>>({});
    const [newOrgName, setNewOrgName] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isCreatingOrg, setIsCreatingOrg] = useState(false);
    const [isMoving, setIsMoving] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [toasts, setToasts] = useState<ToastMessage[]>([]);
    const isActionBusy = isMoving !== null || isCreatingOrg;

    const pushToast = useCallback((text: string, type: "success" | "error" = "success") => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setToasts((prev) => [...prev, { id, text, type }]);
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 3500);
    }, []);

    const fetchUsers = useCallback(async (options?: { silent?: boolean }) => {
        const silent = options?.silent ?? false;
        if (!silent) setIsLoading(true);
        setError(null);
        try {
            const { data, error: reqErr } = await listAdminUsersApiV1AdminUsersGet();
            if (reqErr || !data) {
                const errObj = (reqErr || {}) as { error?: string };
                setError(errObj.error || "Failed to load users");
                return;
            }
            const resData = data as unknown as UsersResponse;
            setUsers(resData.users || []);
            setOrganizations(resData.organizations || []);
        } catch {
            setError("Failed to load users");
        } finally {
            if (!silent) setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const filteredUsers = useMemo(() => {
        if (selectedOrgFilter === "all") return users;
        if (selectedOrgFilter === "unassigned") {
            return users.filter((u) => u.memberships.length === 0);
        }
        const orgId = Number(selectedOrgFilter);
        return users.filter((u) => u.memberships.some((m) => m.orgId === orgId));
    }, [users, selectedOrgFilter]);

    const handleMove = async (user: ClerkUserRow) => {
        if (isActionBusy) return;
        const targetRaw = targetOrgByUser[user.clerkUserId];
        const targetOrgId = Number(targetRaw);
        const sourceOrgRaw = sourceOrgByUser[user.clerkUserId];
        const role = roleByUser[user.clerkUserId] ?? "member";
        const sourceOrgId =
            sourceOrgRaw && sourceOrgRaw !== "none" ? Number(sourceOrgRaw) : null;

        // Remove-only flow: if source is selected and target is empty, remove from source org.
        if (!targetRaw && Number.isInteger(sourceOrgId)) {
            setIsMoving(user.clerkUserId);
            try {
                const { error: reqErr } = await removeMemberApiV1AdminMembersDelete({
                    body: { userId: user.clerkUserId, orgId: sourceOrgId! },
                });
                if (reqErr) {
                    const errObj = (reqErr || {}) as { error?: string };
                    pushToast(errObj.error || "Failed to remove user from organization", "error");
                    return;
                }
                pushToast(`Removed ${user.name} from the selected organization.`, "success");
                await fetchUsers({ silent: true });
                setSourceOrgByUser((prev) => ({ ...prev, [user.clerkUserId]: "none" }));
                setTargetOrgByUser((prev) => ({ ...prev, [user.clerkUserId]: "" }));
            } catch {
                pushToast("Failed to remove user from organization", "error");
            } finally {
                setIsMoving(null);
            }
            return;
        }

        if (!Number.isInteger(targetOrgId)) {
            pushToast("Select a target organization first, or choose a source org to remove.", "error");
            return;
        }

        setIsMoving(user.clerkUserId);
        try {
            const { error: reqErr } = await moveUserOrganizationApiV1AdminUsersPost({
                body: {
                    clerkUserId: user.clerkUserId,
                    targetOrgId: targetOrgId!,
                    sourceOrgId,
                    role,
                },
            });
            if (reqErr) {
                const errObj = (reqErr || {}) as { error?: string };
                pushToast(errObj.error || "Failed to move user", "error");
                return;
            }
            pushToast(`Updated ${user.name}'s organization membership.`, "success");
            await fetchUsers({ silent: true });
            setSourceOrgByUser((prev) => ({ ...prev, [user.clerkUserId]: "none" }));
            setTargetOrgByUser((prev) => ({ ...prev, [user.clerkUserId]: "" }));
            setRoleByUser((prev) => ({ ...prev, [user.clerkUserId]: "member" }));
        } catch {
            pushToast("Failed to update user membership", "error");
        } finally {
            setIsMoving(null);
        }
    };

    const handleCreateOrganization = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isActionBusy) return;
        const name = newOrgName.trim();
        if (!name) return;

        setIsCreatingOrg(true);
        try {
            const { error: reqErr } = await createAdminOrganizationApiV1AdminOrganizationsPost({
                body: { name },
            });
            if (reqErr) {
                const errObj = (reqErr || {}) as { error?: string };
                pushToast(errObj.error || "Failed to create organization", "error");
                return;
            }
            setNewOrgName("");
            pushToast(`Created organization "${name}".`, "success");
            await fetchUsers({ silent: true });
        } catch {
            pushToast("Failed to create organization", "error");
        } finally {
            setIsCreatingOrg(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="fixed right-4 top-4 z-50 space-y-2">
                {toasts.map((toast) => (
                    <div
                        key={toast.id}
                        className={`min-w-[240px] max-w-sm rounded-xl border px-4 py-3 text-sm font-medium shadow-lg backdrop-blur-sm ${
                            toast.type === "success"
                                ? "border-green-200 bg-green-50/95 text-green-800"
                                : "border-red-200 bg-red-50/95 text-red-800"
                        }`}
                    >
                        {toast.text}
                    </div>
                ))}
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                    Organization Setup
                </h2>
                <form onSubmit={handleCreateOrganization} className="flex gap-3">
                    <input
                        value={newOrgName}
                        onChange={(e) => setNewOrgName(e.target.value)}
                        placeholder="New organization name"
                        disabled={isActionBusy}
                        className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                        type="submit"
                        disabled={isActionBusy || !newOrgName.trim()}
                        className="rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                        {isCreatingOrg ? "Creating..." : "Create"}
                    </button>
                </form>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                    <div>
                        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                            <Users className="w-5 h-5 text-indigo-600" />
                            Clerk Users & Organization Mapping
                        </h2>
                        <p className="text-sm text-slate-500 mt-1 font-medium">
                            View all Clerk users, assign membership, and move users across organizations.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <select
                            value={selectedOrgFilter}
                            onChange={(e) => setSelectedOrgFilter(e.target.value)}
                            className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white"
                        >
                            <option value="all">All users</option>
                            <option value="unassigned">Unassigned users</option>
                            {organizations.map((org) => (
                                <option key={org.id} value={String(org.id)}>
                                    {org.name}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => fetchUsers()}
                            disabled={isLoading || isActionBusy}
                            className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
                        </button>
                    </div>
                </div>

                {error && <div className="px-6 py-4 text-sm text-red-600">{error}</div>}

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-50/70 border-y border-slate-200">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">User</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Current Organizations</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Move / Assign</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={3} className="px-4 py-8 text-center text-sm text-slate-500">
                                        Loading users...
                                    </td>
                                </tr>
                            ) : filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="px-4 py-8 text-center text-sm text-slate-500">
                                        No users found.
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map((user) => (
                                    <tr key={user.clerkUserId} className="border-b border-slate-100 align-top">
                                        <td className="px-4 py-4">
                                            <div className="font-semibold text-slate-900">{user.name}</div>
                                            <div className="text-sm text-slate-500">{user.email ?? user.clerkUserId}</div>
                                        </td>
                                        <td className="px-4 py-4">
                                            {user.memberships.length === 0 ? (
                                                <span className="text-sm text-slate-400">No organization</span>
                                            ) : (
                                                <div className="flex flex-wrap gap-2">
                                                    {user.memberships.map((m) => (
                                                        <span
                                                            key={`${user.clerkUserId}-${m.orgId}`}
                                                            className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                                                        >
                                                            {m.orgName} ({m.role})
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            {(() => {
                                                const sourceSelection = sourceOrgByUser[user.clerkUserId] ?? "none";
                                                const targetSelection = targetOrgByUser[user.clerkUserId] ?? "";
                                                const isSourceSelected = sourceSelection !== "none";
                                                const isTargetSelected = targetSelection !== "";
                                                const actionMode = !isTargetSelected && isSourceSelected
                                                    ? "remove"
                                                    : isTargetSelected && isSourceSelected
                                                      ? "move"
                                                      : isTargetSelected && !isSourceSelected
                                                        ? "add"
                                                        : "idle";

                                                const actionHint =
                                                    actionMode === "remove"
                                                        ? "Remove from selected source organization."
                                                        : actionMode === "move"
                                                          ? "Move from source organization to target organization."
                                                          : actionMode === "add"
                                                            ? "Add user to target organization (no removal)."
                                                            : "Pick source and/or target to choose Add, Move, or Remove.";

                                                return (
                                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                                <div className="mb-2 flex flex-wrap items-center gap-2">
                                                    <span
                                                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                                                            actionMode === "remove"
                                                                ? "bg-red-100 text-red-700"
                                                                : actionMode === "move"
                                                                  ? "bg-amber-100 text-amber-700"
                                                                  : actionMode === "add"
                                                                    ? "bg-emerald-100 text-emerald-700"
                                                                    : "bg-slate-200 text-slate-600"
                                                        }`}
                                                    >
                                                        {actionMode === "remove"
                                                            ? "Remove"
                                                            : actionMode === "move"
                                                              ? "Move"
                                                              : actionMode === "add"
                                                                ? "Add"
                                                                : "Action not set"}
                                                    </span>
                                                    <span className="text-[11px] font-medium text-slate-600">{actionHint}</span>
                                                </div>

                                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_1fr] sm:items-end">
                                                    <div className="space-y-1">
                                                        <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                                            Source Organization
                                                        </label>
                                                        <select
                                                            value={sourceOrgByUser[user.clerkUserId] ?? "none"}
                                                            onChange={(e) =>
                                                                setSourceOrgByUser((prev) => ({
                                                                    ...prev,
                                                                    [user.clerkUserId]: e.target.value,
                                                                }))
                                                            }
                                                            disabled={isActionBusy}
                                                            className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs"
                                                        >
                                                            <option value="none">No source (assign only)</option>
                                                            {user.memberships.map((m) => (
                                                                <option key={m.orgId} value={String(m.orgId)}>
                                                                    {m.orgName}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div className="hidden sm:flex justify-center pb-2">
                                                        <MoveRight className="h-4 w-4 text-slate-400" />
                                                    </div>

                                                    <div className="space-y-1">
                                                        <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                                            Target Organization
                                                        </label>
                                                        <select
                                                            value={targetOrgByUser[user.clerkUserId] ?? ""}
                                                            onChange={(e) =>
                                                                setTargetOrgByUser((prev) => ({
                                                                    ...prev,
                                                                    [user.clerkUserId]: e.target.value,
                                                                }))
                                                            }
                                                            disabled={isActionBusy}
                                                            className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs"
                                                        >
                                                            <option value="">Select target org</option>
                                                            {organizations.map((org) => (
                                                                <option key={org.id} value={String(org.id)}>
                                                                    {org.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                                                    <div className="flex items-center gap-2">
                                                        <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                                            Role
                                                        </label>
                                                        <select
                                                            value={roleByUser[user.clerkUserId] ?? "member"}
                                                            onChange={(e) =>
                                                                setRoleByUser((prev) => ({
                                                                    ...prev,
                                                                    [user.clerkUserId]: e.target.value as Role,
                                                                }))
                                                            }
                                                            disabled={isActionBusy}
                                                            className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs"
                                                        >
                                                            <option value="member">Member</option>
                                                            <option value="admin">Admin</option>
                                                        </select>
                                                    </div>
                                                    <button
                                                        onClick={() => handleMove(user)}
                                                        disabled={isActionBusy || actionMode === "idle"}
                                                        className="rounded-lg bg-indigo-600 px-3.5 py-2 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
                                                    >
                                                        {isMoving === user.clerkUserId
                                                            ? "Saving..."
                                                            : actionMode === "remove"
                                                              ? "Remove From Source"
                                                              : actionMode === "add"
                                                                ? "Add To Target"
                                                                : actionMode === "move"
                                                                  ? "Move To Target"
                                                                  : "Save Changes"}
                                                    </button>
                                                </div>
                                            </div>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
